import React, {useState, useMemo} from 'react';
import {Link} from 'react-router-dom';
import {CustomerOrderPositionInterface} from '../../interfaces/customer/CustomerOrderPositionInterface';
import Money from '../core/Money';
import MoneyInterface from '../../interfaces/core/MoneyInterface';
import {formatNumber, getWordPositionEnding} from "../../utils/Utils";
import {BillPositionInterface} from "../../interfaces/customer/BillPositionInterface";

interface OrderProductTableProps {
    positions: CustomerOrderPositionInterface[] | BillPositionInterface[];
}

type SortByField = 'name' | 'code' | 'articul' | 'brand_name' | 'price' | 'customer_order_count';

export default function OrderProductTable({positions}: OrderProductTableProps) {
    const [sortBy, setSortBy] = useState<SortByField>('name');
    const [showExtraPositions, setShowExtraPositions] = useState(false);

    const sortedPositions = useMemo(() => {
        if (!positions) return [];

        return [...positions].sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.product.name.localeCompare(b.product.name);
                case 'code':
                    return a.product.code.localeCompare(b.product.code);
                case 'articul':
                    return a.product.articul.localeCompare(b.product.articul);
                case 'brand_name':
                    return a.product.brand_name.localeCompare(b.product.brand_name);
                case 'price':
                    return ((a.current_price || a.price_final || a.price)?.amount || 0) - ((b.current_price || b.price_final || b.price)?.amount || 0);
                case 'customer_order_count':
                    return (a.customer_order_count || a.cnt) - (b.customer_order_count || b.cnt);
                default:
                    return 0;
            }
        });
    }, [positions, sortBy]);

    const removedStates = positions?.map(p => p.is_removed).join(',');

    const total = useMemo(() => {
        if (!positions || positions.length === 0) return {
            weight: 0,
            volume: 0,
            count: 0,
            price: {amount: 0, currency: {name: 'RUB'}}
        };

        //@ts-ignore
        return positions.reduce((acc: any, position: CustomerOrderPositionInterface | BillPositionInterface) => {
            // Пропускаем удаленные позиции
            if (position.is_removed) {
                return acc;
            }

            const cnt = position.customer_order_count ?? position.cnt_final ?? position.cnt ?? 0;
            const price = (position.current_price || position.price_final || position.price)?.amount || 0;

            return {
                weight: acc.weight + (position.product.weight * cnt),
                volume: acc.volume + (position.product.volume * cnt),
                count: acc.count + cnt,
                price: {
                    amount: acc.price.amount + (price * cnt),
                    currency: (position.current_price || position.price_final || position.price)?.currency || {name: 'RUB'}
                }
            };
        }, {
            weight: 0,
            volume: 0,
            count: 0,
            price: {amount: 0, currency: {name: 'RUB'}}
        });
    }, [positions, removedStates]);


    const visiblePositions = showExtraPositions ? sortedPositions : sortedPositions.slice(0, 10);

    return (<div className="table-responsive">
        <table className="table">
            <thead>
            <tr>
                <th>
                    <a href="javascript:void(0);" onClick={() => setSortBy('name')}>
                        Название
                    </a>
                </th>
                <th className="text-center">
                    <a href="javascript:void(0);" onClick={() => setSortBy('code')}>
                        Код
                    </a>
                </th>
                {/*<th className="text-center">*/}
                {/*    <a href="javascript:void(0);" onClick={() => setSortBy('articul')}>*/}
                {/*        Артикул*/}
                {/*    </a>*/}
                {/*</th>*/}
                {/*<th className="text-center">*/}
                {/*    <a href="javascript:void(0);" onClick={() => setSortBy('brand_name')}>*/}
                {/*        Бренд*/}
                {/*    </a>*/}
                {/*</th>*/}
                <th className="text-end">
                    <a href="javascript:void(0);" onClick={() => setSortBy('price')}>
                        Цена
                    </a>
                </th>
                <th className="text-end">
                    <span className={'text-nowrap'}>Вес/Объем(ед.)</span>
                </th>
                <th className="text-center">
                                <span className={'text-nowrap'}>
                                    <a href="javascript:void(0);" onClick={() => setSortBy('customer_order_count')}>
                                        Кол-во
                                    </a>
                                </span>
                </th>
                <th className="text-end">Стоимость</th>
            </tr>
            </thead>
            <tbody>
            {visiblePositions.map((position, index) => {
                if (!position.product.code) return null;

                const currentPrice = position.current_price || position.price_final || position.price;
                const prevPrice = position.price_prev || position.price;
                const currentCount = position.customer_order_count ?? position.cnt_final ?? position.cnt;
                const prevCount = position.customer_order_count_prev ?? position.cnt;

                const hasPriceChange = currentPrice?.amount && prevPrice?.amount &&
                                     currentPrice.amount !== prevPrice.amount;
                const hasCountChange = currentCount !== prevCount;

                return (
                    <tr key={position.id || index} className={position.is_removed ? 'table-danger' : ''}>
                        <td>
                            <Link to={`/catalog/item-by-code/${position.product.code}`} className={position.is_removed ? 'text-decoration-line-through text-muted' : ''}>
                                {position.product.name}
                            </Link>
                            {' '}
                            <span className={'badge bg-secondary'}>{position.product.brand_name}</span>
                        </td>
                        <td className="text-center">
                            <span>{position.product.code}</span>
                        </td>
                        <td className="text-end">
                            {hasPriceChange && (
                                <>
                                    <span className="text-decoration-line-through text-muted text-nowrap " >
                                        <Money value={prevPrice}/>
                                        <br/>
                                    </span>
                                </>
                            )}
                            <span className={'text-nowrap'}>
                                <Money value={currentPrice}/>
                            </span>
                        </td>
                        <td className="text-end">
                            <span className={'text-nowrap'}>
                                {formatNumber(position.product.weight)} кг.
                                <br/>
                                <small className="text-muted">
                                    {formatNumber(position.product.volume)}
                                </small> м<sup>3</sup>
                            </span>
                        </td>
                        <td className="text-center">
                            <span className={'text-nowrap'}>
                                {hasCountChange && (
                                    <>
                                        <span className="text-decoration-line-through text-muted">
                                            {prevCount}
                                        </span>
                                        {' → '}
                                    </>
                                )}
                                {currentCount} шт.
                                {position.is_new && (
                                    <sup className="text-danger">нов.</sup>
                                )}
                            </span>
                        </td>
                        <td className="text-end">
                            <span className={'text-nowrap'}>
                                <Money value={{
                                    amount: (currentPrice?.amount || 0) * currentCount,
                                    currency: currentPrice?.currency || {name: 'RUB'}
                                } as MoneyInterface}/>
                            </span>
                        </td>
                    </tr>
                );
            })}

            {sortedPositions.length > 10 && (
                <tr>
                    <td colSpan={9} className="text-center text-danger">
                            <span className={'pointer text-underline-dashed'} onClick={() => setShowExtraPositions(!showExtraPositions)} >
                                {showExtraPositions ? 'скрыть' : 'показать еще'} {sortedPositions.length - 10} {getWordPositionEnding(sortedPositions.length - 10)}
                            </span>
                    </td>
                </tr>
            )}

            <tr>
                <th colSpan={3}>ИТОГО:</th>
                <th className="text-end">
                                <span className={'text-nowrap'}>
                                    {formatNumber(total.weight)} кг.
                                    <br/>
                                    <small className="text-muted">
                                        {formatNumber(total.volume)}
                                    </small> м<sup>3</sup>
                                </span>
                </th>
                <th className="text-end">
                    {total.count} шт.
                </th>
                <th className="text-end">
                    <span className={'text-nowrap'}>
                        <Money value={total.price as MoneyInterface}/>
                    </span>
                </th>
            </tr>
            </tbody>
        </table>
    </div>);
}